import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import {
  Bar, BarChart, Line, LineChart, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { ArrowUpRight, ArrowDownRight, Warning, Sparkle, Check, CaretRight } from "@phosphor-icons/react";

const KpiCard = ({ label, value, delta, suffix = "" }) => {
  const positive = (delta ?? 0) >= 0;
  return (
    <div className="bg-white p-5 hover:bg-secondary/40 transition-colors">
      <div className="eos-overline">{label}</div>
      <div className="font-heading font-extrabold text-[34px] mt-2 tracking-tight leading-none">
        {value}<span className="text-base text-muted-foreground">{suffix}</span>
      </div>
      {delta != null && delta !== 0 && (
        <div className={`text-[11px] mt-2 inline-flex items-center gap-1 ${positive ? "text-emerald-700" : "text-rose-700"}`}>
          {positive ? <ArrowUpRight size={12} weight="bold"/> : <ArrowDownRight size={12} weight="bold"/>}
          {Math.abs(delta)}% vs LY
        </div>
      )}
    </div>
  );
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const inst = user?.institution_type || "higher_ed";
  const [k, setK] = useState(null);
  const [charts, setCharts] = useState(null);

  useEffect(() => {
    (async () => {
      const [a, b] = await Promise.all([
        api.get(`/dashboard/kpis?institution_type=${inst}`),
        api.get(`/dashboard/charts?institution_type=${inst}`),
      ]);
      setK(a.data); setCharts(b.data);
    })();
  }, [inst]);

  if (!k) return <div className="p-10 text-sm text-muted-foreground">Loading dashboard…</div>;

  return (
    <div className="p-6 lg:p-8 space-y-8" data-testid="admin-dashboard">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <div className="eos-overline">Executive Dashboard · Admin</div>
          <h1 className="font-heading font-extrabold text-4xl tracking-tight mt-1">
            {inst === "k12" ? "Vidya Springs School · " : "Bharat Institute of Technology · "}
            <span className="text-muted-foreground">AY 2025-26</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Live snapshot across all 13 modules · refreshed just now</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="px-3 py-2 border border-border rounded-md text-[11px] inline-flex items-center gap-1.5">
            <Check size={12} weight="bold" className="text-emerald-600"/> NEP 2020 compliance {k.nep_compliance}%
          </div>
          <div className="px-3 py-2 border border-border rounded-md text-[11px] inline-flex items-center gap-1.5">
            <Sparkle size={12} weight="fill" className="text-[hsl(20_90%_48%)]"/> {k.active_modules} modules active
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-border border border-border rounded-md overflow-hidden" data-testid="kpi-strip">
        <KpiCard label="Enrollment" value={k.enrollment.toLocaleString()} delta={k.enrollment_delta}/>
        <KpiCard label="Attendance" value={k.attendance_rate} delta={k.attendance_delta} suffix="%"/>
        <KpiCard label="Pass Rate" value={k.pass_rate} delta={k.pass_rate_delta} suffix="%"/>
        {inst === "higher_ed" && <KpiCard label="Placement" value={k.placement_rate} delta={k.placement_delta} suffix="%"/>}
        <KpiCard label="Fee Collection" value={`₹${k.fee_collection_amount_cr}`} delta={null} suffix=" Cr"/>
        {inst === "k12" && <KpiCard label="Open Applications" value={k.open_applications}/>}
      </div>

      <div className="grid lg:grid-cols-12 gap-5">
        <div className="lg:col-span-7 eos-card">
          <div className="flex items-end justify-between mb-2">
            <div>
              <div className="eos-overline">Fee Collection · 10-month trend</div>
              <div className="font-heading font-bold text-xl tracking-tight mt-1">₹{k.fee_collection_amount_cr} Cr collected · {k.fee_collection_pct}% of target</div>
            </div>
            <a className="text-xs text-muted-foreground hover:text-foreground" href="/app/fees">View fees →</a>
          </div>
          <div className="h-64 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts?.fee_collection || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))"/>
                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))"/>
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))"/>
                <Tooltip contentStyle={{ borderRadius: 6, fontSize: 12, border: "1px solid hsl(var(--border))" }}/>
                <Bar dataKey="amount" fill="hsl(244 53% 20%)" radius={[2,2,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="lg:col-span-5 eos-card">
          <div className="eos-overline">Enrollment Trend</div>
          <div className="font-heading font-bold text-xl tracking-tight mt-1">{k.enrollment.toLocaleString()} active learners</div>
          <div className="h-64 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={charts?.enrollment_trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))"/>
                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))"/>
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))"/>
                <Tooltip contentStyle={{ borderRadius: 6, fontSize: 12, border: "1px solid hsl(var(--border))" }}/>
                <Line type="monotone" dataKey="count" stroke="hsl(20 90% 48%)" strokeWidth={2.5} dot={{ r: 3 }}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 eos-card">
          <div className="flex items-center justify-between">
            <div className="eos-overline">AI predictions · this week</div>
            <span className="text-[10px] font-mono-eos text-muted-foreground">claude-sonnet-4-5</span>
          </div>
          <div className="mt-4 divide-y divide-border">
            {[
              { tag: "dropout-risk",      label: "Dropout Risk",            count: 142,  hint: "Attendance < 65% across CSE Y2/Y3", tone: "rose" },
              { tag: "fee-default",       label: "Fee-Default Predicted",   count: 87,   hint: "Likely overdue by Mar 15 — recommend EMI nudge", tone: "amber" },
              { tag: "abc-credits-ready", label: "ABC Credits Ready",       count: 1284, hint: "Push to Academic Bank of Credits", tone: "indigo" },
              { tag: "placement-eligible",label: "Placement Eligible",      count: 612,  hint: "CGPA ≥ 7.0 · final year cohort", tone: "emerald" },
            ].map((row) => (
              <Link
                to={`/app/cohort/${row.tag}`}
                key={row.tag}
                className="py-3 flex items-center gap-4 hover:bg-secondary/40 -mx-2 px-2 rounded transition-colors group"
                data-testid={`prediction-row-${row.tag}`}
              >
                <span className={`px-2 py-1 text-[10px] font-medium rounded-md
                  ${row.tone === "rose" && "bg-rose-50 text-rose-700"}
                  ${row.tone === "amber" && "bg-amber-50 text-amber-800"}
                  ${row.tone === "indigo" && "bg-indigo-50 text-indigo-700"}
                  ${row.tone === "emerald" && "bg-emerald-50 text-emerald-700"}`}>{row.label}</span>
                <div className="font-heading font-bold text-lg">{row.count}</div>
                <div className="text-xs text-muted-foreground flex-1">{row.hint}</div>
                <CaretRight size={12} weight="bold" className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"/>
              </Link>
            ))}
          </div>
        </div>

        <div className="eos-card relative overflow-hidden">
          <Warning size={20} weight="duotone" className="text-[hsl(20_90%_48%)]"/>
          <div className="font-heading font-semibold text-base mt-3 leading-tight">Compliance window closing</div>
          <p className="text-sm text-muted-foreground mt-2">NIRF Engineering submission due in 38 days. 92% of fields auto-populated from SIS + LMS.</p>
          <a href="/app/reports" className="mt-4 inline-flex items-center gap-1 text-sm font-medium hover:underline">Open report builder <ArrowUpRight size={14} weight="bold"/></a>
        </div>
      </div>

      <div className="eos-card">
        <div className="eos-overline mb-3">Module adoption · last 30 days</div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-border border border-border rounded-md overflow-hidden">
          {(charts?.module_usage || []).map(m => (
            <div key={m.module} className="bg-white p-4">
              <div className="text-xs text-muted-foreground">{m.module}</div>
              <div className="font-heading font-bold text-2xl mt-1">{m.usage}%</div>
              <div className="mt-2 h-1 bg-border rounded-full overflow-hidden">
                <div className="h-full bg-[hsl(244_53%_20%)]" style={{ width: `${m.usage}%` }}/>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
